// Textos de reportsTopology (FE-03a, R06). Ligado em DICT.<lang>.app.reportsTopology; lido por
// `useApp().t.app.reportsTopology`. Relatório "Topologia da OLT": árvore OLT → placas → PON → CTOs → ONUs,
// diagrama em leque, gaveta de detalhes, XLSX e impressão.

const pt = {
  requestFailed: 'Falha na requisição.',
  backToHub: '← Central de Relatórios',
  refresh: 'Atualizar',
  yes: 'Sim',
  no: 'Não',
  oltStatus: { online: 'Online', offline: 'Offline', unknown: 'Desconhecido' },
  fresh: {
    never: 'nunca coletado',
    minAgo: (n) => `coletado há ${n} min`,
    hourAgo: (n) => `coletado há ${n} h`,
    dayAgo: (n) => `coletado há ${n} d`,
  },
  online: 'online',
  offline: 'offline',
  list: {
    loading: 'Carregando ONUs…',
    none: 'Nenhuma ONU.',
    more: (n) => `+ mostrar mais (${n})`,
    truncated: (total, shown) => `${total} ONUs no total — exibindo as ${shown} primeiras.`,
  },
  fan: {
    portTitle: (slot, port, n) => `Porta ${slot}/${port} · ${n} ONU(s)`,
    resetZoom: 'resetar zoom',
  },
  toast: {
    liveDone: 'Leitura ao vivo concluída',
    livePort: (slot, port) => `Porta ${slot}/${port}`,
    liveFailed: 'Falha na leitura ao vivo',
    notFound: (q) => `Nada encontrado para "${q}".`,
    onuNoPort: 'ONU encontrada, mas a porta não está na árvore.',
    searchFailed: 'Falha na busca',
    exportFailed: 'Falha ao exportar',
  },
  xlsx: {
    notInventoried: '(não inventariada)',
    boardsHead: ['Slot', 'Tipo', 'Tipo real', 'Status', 'SW', 'Temp (°C)', 'Portas'],
    portsHead: ['Slot/Porta', 'Nome', 'Status', 'ONUs online', 'ONUs total', 'Capacidade', 'Ocupação %', 'Rx médio (dBm)'],
    onusHead: ['Serial', 'Nome', 'Cliente', 'Status', 'Online', 'Rx (dBm)', 'ONU-ID', 'Modelo', 'VLAN', 'Distância (m)', 'Slot/Porta'],
    sheetBoards: 'Placas',
    sheetPorts: 'Portas',
    sheetOnus: 'ONUs',
  },
  page: {
    title: 'Topologia da OLT',
    subtitle: 'Árvore OLT → placas → portas PON → CTOs → ONUs, montada a partir da última coleta. As ONUs de cada porta são carregadas sob demanda ao expandir o nó.',
    viewTree: 'Árvore',
    viewDiagram: 'Diagrama',
    searchPlaceholder: 'Buscar serial / cliente / porta (slot/porta)…',
    go: 'Ir',
    onlyProblems: 'Só com problema',
    expand: 'Expandir',
    collapse: 'Recolher',
    oltSearch: 'Buscar OLT…',
    noOlts: 'Nenhuma OLT.',
    selectOlt: 'Selecione uma OLT.',
    loading: 'Carregando topologia…',
    noProblems: 'Nenhum ramo com problema. 🎉',
    printTitle: (name) => `Topologia — ${name}`,
    printSummary: (boards, ports, on, tot, fresh) => `${boards} placas · ${ports} portas PON · ${on}/${tot} ONUs online · ${fresh}`,
    printHead: ['Slot/Porta', 'Nome', 'Status', 'ONUs', 'Capacidade', 'Ocupação %'],
    diagramHint: 'Selecione uma porta PON na árvore para ver o leque splitter → ONUs.',
    drawerHint: 'Clique num nó da árvore para ver os detalhes.',
  },
  tree: {
    slotLabel: (slot, boardType, synthetic) => `Slot ${slot}${boardType ? ` · ${boardType}` : synthetic ? ' · (não inventariada)' : ''}`,
    noCto: 'Sem CTO',
    collapse: 'recolher',
    expand: 'expandir',
  },
  d: {
    vendor: 'Fabricante', model: 'Modelo', status: 'Status', uptime: 'Uptime', boards: 'Placas', ponPorts: 'Portas PON',
    onusOnline: 'ONUs online', boardsPorts: 'Placas / Portas', freshness: 'Frescor', openOlt: 'Abrir OLT',
    slotTitle: (slot) => `Slot ${slot}`, type: 'Tipo', realType: 'Tipo real', sw: 'SW', temperature: 'Temperatura',
    ponPort: 'Porta PON', seeCtos: 'Ver CTOs',
    nameCustomer: 'Nome / Cliente', statusLine: (status, online) => `${status}${online ? ' · online' : ' · offline'}`,
    rxSignal: 'Sinal Rx', onuModel: 'Modelo', vlan: 'VLAN', distance: 'Distância', openOnu: 'Abrir ONU',
    name: 'Nome', occupancy: 'Ocupação', occCap: (on, tot, cap) => `${on} / ${tot} (cap. ${cap})`,
    occupancyPct: 'Ocupação %', avgRx: 'Rx médio', readLive: 'Ler ao vivo', openPort: 'Abrir porta',
    liveTitle: 'Leitura ao vivo', portState: 'Estado da porta', opticalModule: 'Módulo óptico', txLaser: 'Tx (laser)',
    framesRecv: 'Frames recebidos', framesCrc: 'Frames CRC err.',
  },
};

const en = {
  requestFailed: 'Request failed.',
  backToHub: '← Reports Center',
  refresh: 'Refresh',
  yes: 'Yes',
  no: 'No',
  oltStatus: { online: 'Online', offline: 'Offline', unknown: 'Unknown' },
  fresh: {
    never: 'never polled',
    minAgo: (n) => `polled ${n} min ago`,
    hourAgo: (n) => `polled ${n} h ago`,
    dayAgo: (n) => `polled ${n} d ago`,
  },
  online: 'online',
  offline: 'offline',
  list: {
    loading: 'Loading ONUs…',
    none: 'No ONUs.',
    more: (n) => `+ show more (${n})`,
    truncated: (total, shown) => `${total} ONUs in total — showing the first ${shown}.`,
  },
  fan: {
    portTitle: (slot, port, n) => `Port ${slot}/${port} · ${n} ONU(s)`,
    resetZoom: 'reset zoom',
  },
  toast: {
    liveDone: 'Live read completed',
    livePort: (slot, port) => `Port ${slot}/${port}`,
    liveFailed: 'Live read failed',
    notFound: (q) => `Nothing found for "${q}".`,
    onuNoPort: 'ONU found, but its port is not in the tree.',
    searchFailed: 'Search failed',
    exportFailed: 'Export failed',
  },
  xlsx: {
    notInventoried: '(not inventoried)',
    boardsHead: ['Slot', 'Type', 'Real type', 'Status', 'SW', 'Temp (°C)', 'Ports'],
    portsHead: ['Slot/Port', 'Name', 'Status', 'ONUs online', 'ONUs total', 'Capacity', 'Occupancy %', 'Avg Rx (dBm)'],
    onusHead: ['Serial', 'Name', 'Customer', 'Status', 'Online', 'Rx (dBm)', 'ONU-ID', 'Model', 'VLAN', 'Distance (m)', 'Slot/Port'],
    sheetBoards: 'Boards',
    sheetPorts: 'Ports',
    sheetOnus: 'ONUs',
  },
  page: {
    title: 'OLT topology',
    subtitle: 'OLT → boards → PON ports → CTOs → ONUs tree, built from the last poll. The ONUs of each port are loaded on demand when the node is expanded.',
    viewTree: 'Tree',
    viewDiagram: 'Diagram',
    searchPlaceholder: 'Search serial / customer / port (slot/port)…',
    go: 'Go',
    onlyProblems: 'Problems only',
    expand: 'Expand',
    collapse: 'Collapse',
    oltSearch: 'Search OLT…',
    noOlts: 'No OLTs.',
    selectOlt: 'Select an OLT.',
    loading: 'Loading topology…',
    noProblems: 'No branch with problems. 🎉',
    printTitle: (name) => `Topology — ${name}`,
    printSummary: (boards, ports, on, tot, fresh) => `${boards} boards · ${ports} PON ports · ${on}/${tot} ONUs online · ${fresh}`,
    printHead: ['Slot/Port', 'Name', 'Status', 'ONUs', 'Capacity', 'Occupancy %'],
    diagramHint: 'Select a PON port in the tree to see the splitter → ONUs fan.',
    drawerHint: 'Click a node in the tree to see its details.',
  },
  tree: {
    slotLabel: (slot, boardType, synthetic) => `Slot ${slot}${boardType ? ` · ${boardType}` : synthetic ? ' · (not inventoried)' : ''}`,
    noCto: 'No CTO',
    collapse: 'collapse',
    expand: 'expand',
  },
  d: {
    vendor: 'Vendor', model: 'Model', status: 'Status', uptime: 'Uptime', boards: 'Boards', ponPorts: 'PON ports',
    onusOnline: 'ONUs online', boardsPorts: 'Boards / Ports', freshness: 'Freshness', openOlt: 'Open OLT',
    slotTitle: (slot) => `Slot ${slot}`, type: 'Type', realType: 'Real type', sw: 'SW', temperature: 'Temperature',
    ponPort: 'PON port', seeCtos: 'View CTOs',
    nameCustomer: 'Name / Customer', statusLine: (status, online) => `${status}${online ? ' · online' : ' · offline'}`,
    rxSignal: 'Rx signal', onuModel: 'Model', vlan: 'VLAN', distance: 'Distance', openOnu: 'Open ONU',
    name: 'Name', occupancy: 'Occupancy', occCap: (on, tot, cap) => `${on} / ${tot} (cap. ${cap})`,
    occupancyPct: 'Occupancy %', avgRx: 'Avg Rx', readLive: 'Read live', openPort: 'Open port',
    liveTitle: 'Live read', portState: 'Port state', opticalModule: 'Optical module', txLaser: 'Tx (laser)',
    framesRecv: 'Frames received', framesCrc: 'Frames CRC err.',
  },
};

const es = {
  requestFailed: 'Falló la solicitud.',
  backToHub: '← Centro de Reportes',
  refresh: 'Actualizar',
  yes: 'Sí',
  no: 'No',
  oltStatus: { online: 'En línea', offline: 'Fuera de línea', unknown: 'Desconocido' },
  fresh: {
    never: 'nunca sondeada',
    minAgo: (n) => `sondeada hace ${n} min`,
    hourAgo: (n) => `sondeada hace ${n} h`,
    dayAgo: (n) => `sondeada hace ${n} d`,
  },
  online: 'online',
  offline: 'offline',
  list: {
    loading: 'Cargando ONUs…',
    none: 'Ninguna ONU.',
    more: (n) => `+ mostrar más (${n})`,
    truncated: (total, shown) => `${total} ONUs en total — mostrando las primeras ${shown}.`,
  },
  fan: {
    portTitle: (slot, port, n) => `Puerto ${slot}/${port} · ${n} ONU(s)`,
    resetZoom: 'restablecer zoom',
  },
  toast: {
    liveDone: 'Lectura en vivo completada',
    livePort: (slot, port) => `Puerto ${slot}/${port}`,
    liveFailed: 'Falló la lectura en vivo',
    notFound: (q) => `No se encontró nada para "${q}".`,
    onuNoPort: 'ONU encontrada, pero su puerto no está en el árbol.',
    searchFailed: 'Falló la búsqueda',
    exportFailed: 'Falló la exportación',
  },
  xlsx: {
    notInventoried: '(no inventariada)',
    boardsHead: ['Slot', 'Tipo', 'Tipo real', 'Estado', 'SW', 'Temp (°C)', 'Puertos'],
    portsHead: ['Slot/Puerto', 'Nombre', 'Estado', 'ONUs online', 'ONUs total', 'Capacidad', 'Ocupación %', 'Rx medio (dBm)'],
    onusHead: ['Serial', 'Nombre', 'Cliente', 'Estado', 'Online', 'Rx (dBm)', 'ONU-ID', 'Modelo', 'VLAN', 'Distancia (m)', 'Slot/Puerto'],
    sheetBoards: 'Tarjetas',
    sheetPorts: 'Puertos',
    sheetOnus: 'ONUs',
  },
  page: {
    title: 'Topología de la OLT',
    subtitle: 'Árbol OLT → tarjetas → puertos PON → CTOs → ONUs, construido a partir de la última lectura. Las ONUs de cada puerto se cargan bajo demanda al expandir el nodo.',
    viewTree: 'Árbol',
    viewDiagram: 'Diagrama',
    searchPlaceholder: 'Buscar serial / cliente / puerto (slot/puerto)…',
    go: 'Ir',
    onlyProblems: 'Solo con problema',
    expand: 'Expandir',
    collapse: 'Contraer',
    oltSearch: 'Buscar OLT…',
    noOlts: 'Ninguna OLT.',
    selectOlt: 'Seleccione una OLT.',
    loading: 'Cargando topología…',
    noProblems: 'Ninguna rama con problemas. 🎉',
    printTitle: (name) => `Topología — ${name}`,
    printSummary: (boards, ports, on, tot, fresh) => `${boards} tarjetas · ${ports} puertos PON · ${on}/${tot} ONUs online · ${fresh}`,
    printHead: ['Slot/Puerto', 'Nombre', 'Estado', 'ONUs', 'Capacidad', 'Ocupación %'],
    diagramHint: 'Seleccione un puerto PON en el árbol para ver el abanico splitter → ONUs.',
    drawerHint: 'Haga clic en un nodo del árbol para ver los detalles.',
  },
  tree: {
    slotLabel: (slot, boardType, synthetic) => `Slot ${slot}${boardType ? ` · ${boardType}` : synthetic ? ' · (no inventariada)' : ''}`,
    noCto: 'Sin CTO',
    collapse: 'contraer',
    expand: 'expandir',
  },
  d: {
    vendor: 'Fabricante', model: 'Modelo', status: 'Estado', uptime: 'Uptime', boards: 'Tarjetas', ponPorts: 'Puertos PON',
    onusOnline: 'ONUs online', boardsPorts: 'Tarjetas / Puertos', freshness: 'Frescura', openOlt: 'Abrir OLT',
    slotTitle: (slot) => `Slot ${slot}`, type: 'Tipo', realType: 'Tipo real', sw: 'SW', temperature: 'Temperatura',
    ponPort: 'Puerto PON', seeCtos: 'Ver CTOs',
    nameCustomer: 'Nombre / Cliente', statusLine: (status, online) => `${status}${online ? ' · online' : ' · offline'}`,
    rxSignal: 'Señal Rx', onuModel: 'Modelo', vlan: 'VLAN', distance: 'Distancia', openOnu: 'Abrir ONU',
    name: 'Nombre', occupancy: 'Ocupación', occCap: (on, tot, cap) => `${on} / ${tot} (cap. ${cap})`,
    occupancyPct: 'Ocupación %', avgRx: 'Rx medio', readLive: 'Leer en vivo', openPort: 'Abrir puerto',
    liveTitle: 'Lectura en vivo', portState: 'Estado del puerto', opticalModule: 'Módulo óptico', txLaser: 'Tx (láser)',
    framesRecv: 'Frames recibidos', framesCrc: 'Frames CRC err.',
  },
};

export const REPORTS_TOPOLOGY_I18N = { pt, en, es };
